import {Alert, Box, Button, CircularProgress, Dialog, DialogContent, Typography} from "@mui/material";
import I18n from "i18n-js";
import {DialogTitle, globalMessageHandler, Spacer} from "../../../../common";
import React, {useEffect, useState} from "react";
import ChangeEmailRequestForm from "./stages/request/changeEmailRequestForm";
import {ActivationType, validateToken} from "../../../activation/activationApi";
import Activation from "../../../../components/Activation/Activation";
import {changeEmail} from "./changeEmailRequestApi";
import {useNavigate, useParams} from "react-router-dom";
import ActivationSuccess from "../../../../components/Activation/ActivationSuccess";
import ActivationError from "../../../../components/Activation/ActivationError";

export default function ChangeEmailRequestDialog() {
  const [showDialog, setShowDialog] = useState<boolean>(false);
  const [stage, setStage] = useState<string>('request');
  const [email, setEmail] = useState<string>('');
  const [reloadOnClose, setReloadOnClose] = useState<boolean>(false);
  const params = useParams();
  const navigate = useNavigate();

  function closeDialog() {
    setShowDialog(false);
    setStage('request');

    if (reloadOnClose) {
      navigate('/');
      window.location.reload();
      return;
    }
  }

  function onRequestSent(email: string) {
    setStage('activation');
    setEmail(email);
  }

  async function onActivationSuccess(token: string) {
    setShowDialog(true);
    setReloadOnClose(true);
    setStage('loading');

    const response = await changeEmail(token);
    if (response.data.success) {
      setStage('success');
    } else {
      setStage('error');
    }
  }

  async function validateParamsToken() {
    // @ts-ignore It's already validated at this point that params.token is not undefined
    const token: string = params.token;
    const response = await validateToken(token, ActivationType.EMAIL);
    const data = response.data;

    if (!data.success) {
      setShowDialog(true);
      setStage('invalidToken');
    } else {
      onActivationSuccess(token);
    }
  }

  useEffect(() => {
    if (params.token !== undefined) {
      // Hide the default stage until token validation is done
      setStage('loading');

      validateParamsToken();
    }
  }, []);

  return (
    <>
      <Button
        onClick={() => {
          setShowDialog(true);
        }}
      >
        {I18n.t("Profile.changeEmailRequest.buttonText")}
      </Button>
      <Dialog
        open={showDialog}
        onClose={() => {
          closeDialog();
        }}
      >
        <DialogTitle
          onClose={() => {
            closeDialog();
          }}
        >
          {I18n.t("Profile.changeEmailRequest.title")}
        </DialogTitle>
        <DialogContent>
          {
            stage === 'request'
              ? (
                <>
                  <ChangeEmailRequestForm onRequestSent={onRequestSent}/>
                </>
              )
              : ''
          }
          {
            stage === 'activation'
              ? (
                <>
                  <Activation
                    onActivationSuccess={onActivationSuccess}
                    type={ActivationType.EMAIL}
                    email={email}
                    contentOnly={true}
                    message={
                      <>
                        <Spacer h={1}/>
                        <Alert severity="info">
                          <Typography variant="body1" sx={{fontWeight: "bold"}}>
                            {I18n.t("Profile.changeEmailRequest.activation.title")}
                          </Typography>
                          <Typography variant="body1">
                            {I18n.t("Profile.changeEmailRequest.activation.description")} <br/>
                            {I18n.t("Profile.changeEmailRequest.activation.description2")}
                          </Typography>
                        </Alert>
                        <Spacer h={1}/>
                      </>
                    }
                  />
                </>
              )
              : ''
          }
          {
            stage === 'success'
              ? (
                <>
                  <ActivationSuccess
                    messageCategory={"Profile.changeEmailSuccess"}
                    onBack={closeDialog}
                    onlyContent={true}
                  />
                </>
              )
              : ''
          }
          {
            stage === 'error'
              ? (
                <>
                  <ActivationError
                    messageCategory={"Profile.changeEmailError"}
                    onBack={closeDialog}
                    onlyContent={true}
                  />
                </>
              )
              : ''
          }
          {
            stage === 'invalidToken'
              ? (
                <>
                  <ActivationError
                    messageCategory={"Activation.invalidTokenPage"}
                    onBack={closeDialog}
                    onlyContent={true}
                  />
                </>
              )
              : ''
          }
          {
            stage === 'loading'
              ? (
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                  }}
                >
                  <CircularProgress/>
                </Box>
              )
              : ''
          }
        </DialogContent>
      </Dialog>
    </>
  );
}